import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { GAME_RESULT } from '@ezugi/constants';
import { useOrientationChange } from '@ezugi/hooks';
import { GameFooter } from '@ezugi/ui/components/GameFooter/GameFooter';
import { DeviceInfo } from '@ezugi/utils';

import { useAmount } from '../../../../hooks/useAmount';
import { useIntl } from '../../../../hooks/useIntl';
import {
  roundStatusSelector,
  totalBetSelector,
  winAmountSelector,
} from '../../../../store/selectors';
import { setSpecificAmountFormatTypes, displayAmount } from '../../utils';

const intlSpec = {
  keys: {
    winLabel: 'general.win',
    betLabel: 'general.bet',
  },
};

const WinAmount = () => {
  const winAmount = useSelector(winAmountSelector);
  const gameStatus = useSelector(roundStatusSelector);
  const totalBet = useSelector(totalBetSelector);

  const isWinAmountVisible = gameStatus === GAME_RESULT && !!winAmount;

  const orientation = useOrientationChange();
  const isPortraitSpecific = DeviceInfo.isHandheld && orientation === 'portrait';

  const { winAmountDisplay } = useAmount({
    ...(isPortraitSpecific && {
      format: {
        betFormat: setSpecificAmountFormatTypes(winAmount ?? 0),
      },
    }),
    values: {
      winAmountDisplay: {
        value: winAmount,
        type: 'bet',
        showCurrencySymbol: true,
        showCurrencyCode: false,
      },
    },
  });

  const { totalBetAmount } = useAmount({
    ...(isPortraitSpecific && {
      format: {
        betFormat: setSpecificAmountFormatTypes(totalBet),
      },
    }),
    values: {
      totalBetAmount: {
        value: totalBet,
        type: 'bet',
        showCurrencySymbol: true,
        showCurrencyCode: false,
      },
    },
  });

  const displayedTotalBet = useMemo(
    () => displayAmount(isPortraitSpecific, totalBet, totalBetAmount),
    [ isPortraitSpecific, totalBetAmount, totalBet ]
  );

  const { winLabel, betLabel } = useIntl(intlSpec);

  if (isWinAmountVisible) {
    return <GameFooter.Text data-e2e="win-amount" label={winLabel} value={winAmountDisplay} />;
  }

  return <GameFooter.Text data-e2e="total-bet" label={betLabel} value={displayedTotalBet} />;
};

export default memo(WinAmount);
